import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
const validateEnvironment = () => {
  if (!supabaseUrl || !supabaseAnonKey) {
    console.error('Missing Supabase environment variables:', {
      url: supabaseUrl ? 'Set' : 'Missing',
      key: supabaseAnonKey ? 'Set' : 'Missing'
    });
    
    throw new Error(
      'Missing Supabase environment variables. Please check your .env file.\n' +
      'Required variables: VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY\n' +
      'Make sure to copy .env.example to .env and fill in your Supabase credentials.'
    );
  }
  
  if (supabaseUrl === 'your_supabase_project_url' || supabaseAnonKey === 'your_supabase_anon_key') {
    throw new Error(
      'Supabase environment variables are not configured properly.\n' +
      'Please replace the placeholder values in your .env file with your actual Supabase credentials.'
    );
  }
};

// Create Supabase client with proper validation
let supabase: SupabaseClient;

try {
  validateEnvironment();
  console.log('Initializing Supabase client with:', {
    url: supabaseUrl.substring(0, 15) + '...',
    keyLength: supabaseAnonKey.length
  });
  
  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true
    }
  });
  
  // Test the connection
  supabase.auth.getSession()
    .then(() => console.log('Supabase connection successful'))
    .catch(err => console.error('Supabase connection test failed:', err));
    
} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
  
  // Create a mock client that will throw helpful errors
  const mockClient = {
    auth: {
      getSession: () => Promise.reject(new Error('Supabase client not properly initialized. Check your environment variables.')),
      signUp: () => Promise.reject(new Error('Supabase client not properly initialized. Check your environment variables.')),
      signInWithPassword: () => Promise.reject(new Error('Supabase client not properly initialized. Check your environment variables.')),
      signOut: () => Promise.reject(new Error('Supabase client not properly initialized. Check your environment variables.')),
      onAuthStateChange: () => ({ data: { subscription: null }, unsubscribe: () => {} })
    },
    from: () => ({
      select: () => Promise.reject(new Error('Supabase client not properly initialized. Check your environment variables.')),
      insert: () => Promise.reject(new Error('Supabase client not properly initialized. Check your environment variables.')),
      update: () => Promise.reject(new Error('Supabase client not properly initialized. Check your environment variables.')),
      delete: () => Promise.reject(new Error('Supabase client not properly initialized. Check your environment variables.')),
      upsert: () => Promise.reject(new Error('Supabase client not properly initialized. Check your environment variables.'))
    }),
    rpc: () => Promise.reject(new Error('Supabase client not properly initialized. Check your environment variables.')),
    channel: () => ({
      on: () => ({ subscribe: () => {} }),
      subscribe: () => {},
      unsubscribe: () => {}
    })
  } as any;
  
  supabase = mockClient;
}

export { supabase };

// Database types
export interface Database {
  public: {
    Tables: {
      users: {
        Row: {
          id: string;
          email: string;
          name: string;
          role: 'ADMIN' | 'VIEWER';
          google_id: string | null;
          password_hash: string | null;
          avatar: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          email: string;
          name: string;
          role?: 'ADMIN' | 'VIEWER';
          google_id?: string | null;
          password_hash?: string | null;
          avatar?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          email?: string;
          name?: string;
          role?: 'ADMIN' | 'VIEWER';
          google_id?: string | null;
          password_hash?: string | null;
          avatar?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      rooms: {
        Row: {
          id: string;
          name: string;
          current_state_id: string | null;
          size: 'small' | 'medium' | 'large';
          view_mode: 'minimal' | 'detailed';
          auto_reset_state_id: string | null;
          auto_reset_minutes: number | null;
          created_by: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          current_state_id?: string | null;
          size?: 'small' | 'medium' | 'large';
          view_mode?: 'minimal' | 'detailed';
          auto_reset_state_id?: string | null;
          auto_reset_minutes?: number | null;
          created_by: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          current_state_id?: string | null;
          size?: 'small' | 'medium' | 'large';
          view_mode?: 'minimal' | 'detailed';
          auto_reset_state_id?: string | null;
          auto_reset_minutes?: number | null;
          created_by?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
      room_states: {
        Row: {
          id: string;
          room_id: string;
          name: string;
          color: string;
          emoji: string | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          room_id: string;
          name: string;
          color: string;
          emoji?: string | null;
          created_at?: string;
        };
        Update: {
          id?: string;
          room_id?: string;
          name?: string;
          color?: string;
          emoji?: string | null;
          created_at?: string;
        };
      };
      room_members: {
        Row: {
          id: string;
          room_id: string;
          user_id: string;
          role: 'ADMIN' | 'USER' | 'VIEWER';
          joined_at: string;
        };
        Insert: {
          id?: string;
          room_id: string;
          user_id: string;
          role?: 'ADMIN' | 'USER' | 'VIEWER';
          joined_at?: string;
        };
        Update: {
          id?: string;
          room_id?: string;
          user_id?: string;
          role?: 'ADMIN' | 'USER' | 'VIEWER';
          joined_at?: string;
        };
      };
      broadcast_links: {
        Row: {
          id: string;
          room_id: string;
          name: string;
          active: boolean;
          created_at: string;
          expires_at: string | null;
          timeout_minutes: number | null;
        };
        Insert: {
          id?: string;
          room_id: string;
          name?: string;
          active?: boolean;
          created_at?: string;
          expires_at?: string | null;
          timeout_minutes?: number | null;
        };
        Update: {
          id?: string;
          room_id?: string;
          name?: string;
          active?: boolean;
          created_at?: string;
          expires_at?: string | null;
          timeout_minutes?: number | null;
        };
      };
      room_state_history: {
        Row: {
          id: string;
          room_id: string;
          state_id: string;
          changed_by: string;
          changed_at: string;
        };
        Insert: {
          id?: string;
          room_id: string;
          state_id: string;
          changed_by: string;
          changed_at?: string;
        };
        Update: {
          id?: string;
          room_id?: string;
          state_id?: string;
          changed_by?: string;
          changed_at?: string;
        };
      };
    };
  };
}